import { SearchLg } from "@untitled-ui/icons-react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import circularBackground from "@/assets/images/circular-lines.png";
import dotGridBackground from "@/assets/images/dotgrid-back.png";
import gridLinesBackground from "@/assets/images/gridlines-back.png";

import boxSvg from "../../assets/svgs/empty-state/box.svg";
import cloudSvg from "../../assets/svgs/empty-state/cloud.svg";
import creditCardSvg from "../../assets/svgs/empty-state/credit-card.svg";
import documentSvg from "../../assets/svgs/empty-state/document.svg";
import FeatureIcon from "../helpers/FeatureIcon";

const backgroundImages = {
  circular: circularBackground,
  grid: gridLinesBackground,
  dotGrid: dotGridBackground,
  none: "",
};

const variants = {
  cloud: cloudSvg,
  creditCard: creditCardSvg,
  document: documentSvg,
  box: boxSvg,
};

const sizeConfigs = {
  sm: {
    image: "p-4",
    title: "text-md",
    description: "text-sm",
    icon: "size-12",
  },
  md: {
    image: "p-6",
    title: "text-lg",
    description: "text-sm",
    icon: "size-14",
  },
  lg: {
    image: "p-8",
    title: "text-xl",
    description: "text-md",
    icon: "size-14",
  },
};

interface EmptyComponentProps {
  background?: keyof typeof backgroundImages;
  variant?: keyof typeof variants;
  icon?: React.ReactNode;
  title?: string;
  description?: string;
  footer?: React.ReactNode;
  size?: "sm" | "md" | "lg";
}

const defaultIcon = (
  <FeatureIcon size="sm" color="white">
    <div className="bg-primary-700/40 flex size-12 items-center justify-center rounded-full text-white">
      <SearchLg className="size-6" />
    </div>
  </FeatureIcon>
);

export default function EmptyComponent({
  background = "dotGrid",
  variant,
  icon,
  title,
  description,
  footer,
  size = "md",
}: EmptyComponentProps) {
  const { t } = useTranslation("core.shared");

  const renderIcon = () => {
    if (!icon) return defaultIcon;

    if (!variant) {
      return (
        <FeatureIcon
          size="lg"
          variant="modern"
          className="bg-white text-gray-700"
        >
          {icon}
        </FeatureIcon>
      );
    }

    return (
      <FeatureIcon size="sm" color="white">
        <div
          className={classNames(
            "bg-primary-700/40 flex items-center justify-center rounded-full text-white",
            sizeConfigs[size].icon,
          )}
        >
          {icon}
        </div>
      </FeatureIcon>
    );
  };

  return (
    <div className="w-full py-5">
      <div
        className={classNames(
          "relative flex flex-col items-center justify-center overflow-hidden rounded-lg",
        )}
      >
        {background !== "none" && (
          <img
            src={backgroundImages[background]}
            alt="background"
            className="absolute inset-0 h-full w-full object-contain"
          />
        )}

        <div
          className={classNames(
            "relative z-10 flex flex-col items-center justify-center",
            !variant && "gap-y-4",
          )}
        >
          {variant ? (
            <div className="relative flex items-center justify-center">
              <img
                src={variants[variant]}
                alt="variant"
                className={sizeConfigs[size].image}
              />
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2">
                {renderIcon()}
              </div>
            </div>
          ) : (
            <div
              className={classNames(
                "flex items-center justify-center",
                icon ? "pt-16" : undefined,
              )}
            >
              {renderIcon()}
            </div>
          )}

          <div className="flex flex-col items-center gap-y-1 pt-4">
            <span
              className={classNames(
                "font-semibold text-gray-900",
                sizeConfigs[size].title,
              )}
            >
              {title || t("No data found")}
            </span>
            <span
              className={classNames(
                "text-gray-600",
                sizeConfigs[size].description,
              )}
            >
              {description !== undefined
                ? description
                : t("No data found. Please try again.")}
            </span>
          </div>
        </div>

        {footer && (
          <div className="relative z-20 flex gap-2 pt-4">{footer}</div>
        )}
      </div>
    </div>
  );
}
